import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Button from './Button';
import { useFlexibleTranslation } from '../contexts/LanguageContext';
import { phTeamMemberInvited } from '../utils/posthogEvents';

const DialogOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
`;

const DialogContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  width: 600px;
  max-width: 90vw;
  max-height: 85vh;
  overflow-y: auto;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

const DialogHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const DialogTitle = styled.h2`
  margin: 0;
  color: #333;
  font-size: 20px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  padding: 4px;
  
  &:hover {
    color: #333;
  }
`;

const UserList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 24px;
`;

const UserItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const UserAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #ff6b35;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
  font-weight: 500;
`;

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

const UserName = styled.span`
  font-weight: 500;
  color: #2d3748;
`;

const UserEmail = styled.span`
  font-size: 14px;
  color: #718096;
`;

const UserRole = styled.select`
  padding: 6px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 14px;
  background-color: white;
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #ff6b35;
    box-shadow: 0 0 0 2px rgba(255, 107, 53, 0.2);
  }
`;

const AddUserButton = styled(Button)`
  width: 100%;
  margin-bottom: 24px;
`;

const AddUserForm = styled.div`
  margin-bottom: 24px;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
`;

const FormRow = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Input = styled.input`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 14px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #ff6b35;
    box-shadow: 0 0 0 2px rgba(255, 107, 53, 0.2);
  }
`;

const FormActions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

const ProjectTitle = styled.div`
  font-size: 16px;
  color: #666;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e0e0e0;
`;

const UsersDialog = ({ isOpen, onClose, currentUser, selectedProject }) => {
  const { t } = useFlexibleTranslation();
  const [users, setUsers] = useState([]);
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    role: 'member'
  });

  // Initialize with project members
  useEffect(() => {
    if (selectedProject && currentUser) {
      // TODO: Fetch project members from backend
      // For now, we'll initialize with the current user as admin
      const displayName = currentUser.profile?.name || 'Current User';
      setUsers([{
        id: currentUser.id || 'current',
        name: displayName,
        email: currentUser.profile?.email || currentUser.email,
        role: 'admin',
        projectId: selectedProject.id
      }]);
    } else {
      setUsers([]);
  }
}, [selectedProject, currentUser]);

  if (!isOpen) return null;

  const handleRoleChange = (userId, newRole) => {
    setUsers(users.map(user => 
      user.id === userId ? { ...user, role: newRole } : user
    ));
    // TODO: Update user role in backend for this project
  };

  const handleAddUser = () => {
    if (!newUser.name || !newUser.email || !selectedProject) return;

    const userToAdd = {
      id: Date.now().toString(), // Temporary ID until backend integration
      ...newUser,
      projectId: selectedProject.id
    };

    setUsers([...users, userToAdd]);
    setNewUser({ name: '', email: '', role: 'member' });
    setIsAddingUser(false);
    phTeamMemberInvited({ projectId: selectedProject.id, inviteeCount: 1 });
    // TODO: Add user to project in backend
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewUser(prev => ({ ...prev, [name]: value }));
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase();
  };

  return (
    <DialogOverlay onClick={onClose}>
      <DialogContent onClick={e => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>{t('usersDialog.projectMembers', 'Project Members')}</DialogTitle>
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </DialogHeader>

        {selectedProject ? (
          <>
            <ProjectTitle>
              {t('usersDialog.managingMembersFor', 'Managing members for')}: <strong>{selectedProject.name}</strong>
            </ProjectTitle>

            {!isAddingUser ? (
              <AddUserButton variant="outline" onClick={() => setIsAddingUser(true)}>
                <svg viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '8px' }}>
                  <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                </svg>
                {t('usersDialog.addProjectMember', 'Add Project Member')}
              </AddUserButton>
            ) : (
              <AddUserForm>
                <FormRow>
                  <Input
                    type="text"
                    name="name"
                    placeholder={t('usersDialog.fullName', 'Full Name')}
                    value={newUser.name}
                    onChange={handleInputChange}
                  />
                </FormRow>
                <FormRow>
                  <Input
                    type="email"
                    name="email"
                    placeholder={t('usersDialog.emailAddress', 'Email Address')}
                    value={newUser.email}
                    onChange={handleInputChange}
                  />
                </FormRow>
                <FormRow>
                  <UserRole
                    name="role"
                    value={newUser.role}
                    onChange={handleInputChange}
                  >
                    <option value="admin">{t('usersDialog.admin', 'Admin')}</option>
                    <option value="member">{t('usersDialog.member', 'Member')}</option>
                    <option value="viewer">{t('usersDialog.viewer', 'Viewer')}</option>
                  </UserRole>
                </FormRow>
                <FormActions>
                  <Button variant="outline" onClick={() => setIsAddingUser(false)}>
                    {t('common.cancel', 'Cancel')}
                  </Button>
                  <Button onClick={handleAddUser} disabled={!newUser.name || !newUser.email}>
                    {t('usersDialog.addMember', 'Add Member')}
                  </Button>
                </FormActions>
              </AddUserForm>
            )}

            <UserList>
              {users.map(user => (
                <UserItem key={user.id}>
                  <UserInfo>
                    <UserAvatar>{getInitials(user.name)}</UserAvatar>
                    <UserDetails>
                      <UserName>{user.name}</UserName>
                      <UserEmail>{user.email}</UserEmail>
                    </UserDetails>
                  </UserInfo>
                  <UserRole
                    value={user.role}
                    onChange={(e) => handleRoleChange(user.id, e.target.value)}
                    disabled={user.id === 'current'} // Disable role change for current user
                  >
                    <option value="admin">{t('usersDialog.admin', 'Admin')}</option>
                    <option value="member">{t('usersDialog.member', 'Member')}</option>
                    <option value="viewer">{t('usersDialog.viewer', 'Viewer')}</option>
                  </UserRole>
                </UserItem>
              ))}
            </UserList>
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <p>{t('usersDialog.selectProjectToManage', 'Please select a project to manage its members.')}</p>
          </div>
        )}

        <Button onClick={onClose}>
          {t('common.close', 'Close')}
        </Button>
      </DialogContent>
    </DialogOverlay>
  );
};

export default UsersDialog; 